/** Step-by-step buying guide, including how to read a card's condition, finish and language. */
import type { InfoDoc } from "../types";

export const buyingGuide: InfoDoc = {
  es: {
    title: "Guía para comprar",
    intro: "Todo lo que necesitás saber para comprar en pokefans.fun, paso a paso.",
    sections: [
      {
        title: "1. Encontrá lo que buscás",
        paragraphs: [
          "En el [[/mercado|mercado]] podés buscar por nombre, número o vendedor y filtrar por categoría, estado, idioma, precio y vendedor. Cada carta aparece una vez, con el precio más bajo; tocala para ver todas las ofertas.",
          "¿Tenés una carta y no sabés cuál es? Sacale una foto en [[/identificar|Identificar carta]] y te mostramos las coincidencias.",
        ],
      },
      {
        title: "2. Leé la ficha",
        paragraphs: [
          "Cada carta indica estado, terminación e idioma. El precio depende del estado: una carta jugada cuesta menos que una Near Mint.",
          "Near Mint (NM): como nueva, puede tener detalles mínimos de fábrica. Poco jugada (LP): leves marcas en bordes o superficie. Jugada (MP): desgaste visible, bordes gastados. Muy jugada (HP): desgaste fuerte, dobleces leves. Dañada: roturas, dobleces marcados o manchas.",
          "Terminación: normal, holo (la ilustración brilla), reverse holo (brilla todo menos la ilustración) o primera edición. Idioma: inglés o español.",
          "En los productos sellados (sobres, cajas, ETB) te contamos qué trae cada uno y qué cartas pueden salir.",
          "Algunas fotos son de referencia del catálogo; lo que vale es el estado indicado en la ficha.",
        ],
      },
      {
        title: "3. Precios y moneda",
        paragraphs: [
          "Los precios están en dólares. Con el selector US$ / AR$ del encabezado los ves en pesos: se calculan con el dólar blue del día más un 5%, y son una referencia.",
          "Si pagás con Mercado Pago se cobra en pesos, con la cotización del momento del pago. Con tarjeta internacional o PayPal, en dólares.",
        ],
      },
      {
        title: "4. Carrito y cuenta",
        paragraphs: [
          "Agregá los productos al carrito y, cuando quieras, tocá Finalizar compra. Son tres pasos (tus datos, envío o retiro, y pago) y no hace falta cuenta. Si tenés una, ingresá para usar tus datos guardados y ver todos tus pedidos en Mi cuenta.",
        ],
      },
      {
        title: "5. Datos, entrega y pago",
        paragraphs: [
          "Completá tus datos (el teléfono es para escribirte por WhatsApp) y la dirección de entrega. A CABA y al Gran Buenos Aires lo despachamos dentro de las 48 horas; al resto del país lo coordinamos por WhatsApp. Más en [[/envios|envíos]].",
          "Elegí cómo pagar: Mercado Pago (hasta 12 cuotas), tarjeta internacional, PayPal, transferencia bancaria o cripto (USDT), estas dos con descuento. Tu pedido queda reservado 30 minutos mientras pagás (2 horas si pagás por transferencia o cripto, para que nos mandes el comprobante por WhatsApp); si no se completa el pago, se libera solo.",
        ],
      },
      {
        title: "6. Después de pagar",
        paragraphs: [
          "Te llega un email con la confirmación. Vas a poder ver el estado del pedido en Mi cuenta, y cuando lo despachemos te avisamos por email con el número de seguimiento.",
          "Si te arrepentís, tenés 10 días desde que lo recibís: usá el [[/arrepentimiento|botón de arrepentimiento]]. Más detalles en la [[/reembolsos|política de reembolsos]].",
        ],
      },
    ],
  },
  en: {
    title: "Buying guide",
    intro: "Everything you need to know to buy at pokefans.fun, step by step.",
    sections: [
      {
        title: "1. Find what you want",
        paragraphs: [
          "In the [[/mercado|market]] you can search by name, number or seller and filter by category, condition, language, price and seller. Each card shows once, at its lowest price; open it to see every offer.",
          "Have a card and don't know which one it is? Take a photo in [[/identificar|Identify a card]] and we'll show you the matches.",
        ],
      },
      {
        title: "2. Read the listing",
        paragraphs: [
          "Each card states its condition, finish and language. The price depends on the condition: a played card costs less than a Near Mint one.",
          "Near Mint (NM): like new, may have minimal factory flaws. Lightly Played (LP): light edge or surface wear. Moderately Played (MP): visible wear, worn edges. Heavily Played (HP): heavy wear, light creases. Damaged: tears, strong creases or stains.",
          "Finish: normal, holo (the artwork shines), reverse holo (everything but the artwork shines) or first edition. Language: English or Spanish.",
          "For sealed products (packs, boxes, ETBs) we list what's inside and which cards can be pulled.",
          "Some photos are catalog reference images; the condition in the listing is what counts.",
        ],
      },
      {
        title: "3. Prices and currency",
        paragraphs: [
          "Prices are in US dollars. With the US$ / AR$ switch in the header you see them in pesos, at the day's dólar blue rate plus 5%, as a reference.",
          "Mercado Pago charges in pesos at the rate when you pay. International cards and PayPal charge in dollars.",
        ],
      },
      {
        title: "4. Cart and account",
        paragraphs: ["Add products to the cart and tap Checkout when you're ready. It takes three steps (your details, shipping or pickup, and payment) and no account is needed. If you have one, sign in to use your saved details and see all your orders in My account."],
      },
      {
        title: "5. Details, delivery and payment",
        paragraphs: [
          "Fill in your details (the phone is to reach you on WhatsApp) and the delivery address. To Buenos Aires City and Greater Buenos Aires we dispatch within 48 hours; elsewhere we arrange it on WhatsApp. More on [[/envios|shipping]].",
          "Choose how to pay: Mercado Pago (up to 12 installments), international card, PayPal, bank transfer or crypto (USDT), the last two with a discount. Your order is held for 30 minutes while you pay (2 hours for transfer or crypto, so you can send us the receipt on WhatsApp); if the payment isn't completed, it is released automatically.",
        ],
      },
      {
        title: "6. After paying",
        paragraphs: [
          "You get a confirmation email. You can follow the order in My account, and when we ship it we email you the tracking number.",
          "Changed your mind? You have 10 days from delivery: use the [[/arrepentimiento|withdrawal button]]. More in the [[/reembolsos|refund policy]].",
        ],
      },
    ],
  },
};
